import { fireToast } from '../../../../../../helpers/Toasts';
import { PAGE } from '../../../../../../helpers/pages';
import { pushToDataLayer } from '../../../../../../services/gtmService';
import { useBraze } from '../../../../../../hooks/useBraze';
import { useGetWhatsAppLink } from './useGetWhatsAppLink';
import { usePricingIndicatorLeadTracking } from '../../../../../PricingIndicator/usePricingIndicatorLeadTracking';
import { SELLER_TYPES } from '../../../types';
import { logger } from '@dist-marketplace-frontend/services';

type UseWhatsAppActionArgs = {
  adId: number;
  sectionName?: string;
  price?: string | null;
  priceEuro?: number | null;
  sellerType?: string;
};

const useWhatsappAction = (args: UseWhatsAppActionArgs) => {
  const { adId, sectionName, price, priceEuro, sellerType } = args;

  const { logBrazeWhatsAppEvent } = useBraze();

  const {
    data: cachedWhatsAppLink,
    refetch: getWhatsAppLink,
    isFetching,
  } = useGetWhatsAppLink(adId, { enabled: false });

  const { trackPricingIndicatorLead } = usePricingIndicatorLeadTracking({
    page: PAGE.AD_DETAILS,
  });

  const onWhatsAppButtonClick = async () => {
    pushToDataLayer({ event: 'whatsapp_click' });
    logBrazeWhatsAppEvent({
      ad_id: adId.toString(),
      ad_price: (priceEuro ?? '').toString(),
      seller_type: sellerType ?? '',
    });
    trackPricingIndicatorLead({
      sectionName,
      sellerType: SELLER_TYPES.DEALER,
      price,
    });

    // Using window.open instead of router.push to prevent navigation from killing
    // the in-flight Convert SDK tracking request. Revert to router.push once the
    // pricing indicator experiment is over.
    if (cachedWhatsAppLink) {
      window.open(cachedWhatsAppLink, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const response = await getWhatsAppLink();

      // Handled already in the global query error handler
      if (response.error) return;

      if (!response.data) {
        throw new Error('No WhatsApp link found');
      }
      window.open(response.data, '_blank', 'noopener,noreferrer');
    } catch (error) {
      logger.error({
        error:
          error instanceof Error
            ? error
            : new Error('Unknown error while getting WhatsApp link'),
        message: 'onWhatsAppButtonClick',
        tags: [PAGE.AD_DETAILS],
      });
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
    }
  };

  return {
    onWhatsAppButtonClick,
    isLoading: isFetching,
  };
};

export { useWhatsappAction };
export type { UseWhatsAppActionArgs };
